import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Soft wash, vinyl siding, two storeys, 2,400 sq ft, mildew treatment on the north side', amount: '$450' },
  { item: 'Driveway, broom-finished concrete, 1,100 sq ft, surface cleaner and post-treat', amount: '$180' },
  { item: 'Wood deck, 320 sq ft, low-pressure wash and brightener (no sealing)', amount: '$240' },
  { item: 'Optional: gutter faces brightened, front and garage side, 90 linear ft', amount: '$90' },
];

const faq = [
  {
    q: 'Is this pressure washing quote template free?',
    a: 'Yes. Read it here, download the PDF or copy the wording. No form and no email address needed.',
  },
  {
    q: 'Should I price pressure washing by the square foot?',
    a: 'Square footage is the most common basis for flatwork and siding, adjusted for height, condition and access. The template has a size column on every surface line so the customer can see what you measured.',
  },
  {
    q: 'Why does the template ask about the water source?',
    a: 'Because a missing or weak outside tap can stop the job. Asking up front, and writing who supplies water, avoids a wasted trip.',
  },
  {
    q: 'Can my customer sign this online?',
    a: 'Build the same quote in Scopie and it is emailed to the customer as a link with your business name. They tick any extras, such as the gutter faces, read your terms and sign on their phone.',
  },
];

const page: TemplatePage = {
  slug: 'pressure-washing-quote-template',
  path: '/templates/pressure-washing-quote-template',
  primaryKeyword: 'pressure washing quote template',
  secondaryKeywords: ['pressure washing estimate template', 'power washing quote form', 'soft wash quote template'],
  title: 'Free Pressure Washing Quote Template (PDF) | Scopie', // 51
  metaDescription:
    'A free pressure washing quote template with a line per surface, size and cleaning method, plus the plant, paint and water clauses exterior cleaners need.', // 154
  h1: 'Pressure washing quote template',
  answerFirst:
    'A pressure washing quote works best when every surface has its own line with its size and the cleaning method, followed by the terms that protect you outdoors: plants, old paint, water supply and weather. This free template lays that out, with a filled-in example for a house, driveway and deck.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'document',
      id: 'template',
      h2: 'The quote sheet',
      intro: 'Fill in the brackets and remove surfaces you are not cleaning.',
      pdfName: 'pressure-washing-quote-template',
      sections: [
        { heading: 'Company', lines: ['[Company name] · [Phone] · [Email]', '[Insurance carrier and policy number, if you show it]'] },
        { heading: 'Customer', lines: ['[Name] · [Property address]', 'Quote [number] · Issued [date] · Good until [date]'] },
        {
          heading: 'Surfaces',
          lines: [
            'House siding: [material], [storeys], about [sq ft] · method [soft wash / pressure] · [price]',
            'Roof: [material], [pitch] · soft wash only · [price]',
            'Driveway: [concrete / pavers / asphalt], about [sq ft] · [price]',
            'Walks, steps and patio: about [sq ft] · [price]',
            'Deck or fence: [wood / composite], about [sq ft or linear ft] · [price]',
            'Extras: [gutter faces / rust or oil stain treatment / paver re-sanding] · [price each]',
            'Subtotal [amount] · Tax [amount] · Total [amount]',
          ],
        },
        {
          heading: 'Site conditions',
          lines: ['Water: outside tap at [location] provided by the customer [yes / no]', 'Customer to close windows, move vehicles and outdoor furniture [yes / no]', 'Pets kept inside during cleaning [yes / no]'],
        },
        {
          heading: 'Terms',
          lines: [
            'Plants near treated areas are pre-wet and rinsed; we are not responsible for [your wording].',
            'Oxidized, chalking or failing paint may come off during cleaning.',
            'Some stains (rust, oil, fertilizer, hard water) may lighten but not fully disappear.',
            'Weather: rain or freezing temperatures move the date; the price stays the same.',
            'Deposit: [amount or none]. Cancellation: [your terms].',
          ],
        },
        { heading: 'Accepted by', lines: ['Name [name] · Signature [signature] · Date [date]'] },
      ],
    },
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'Filled in for a typical house',
      label: 'Example: siding, driveway and deck',
      lines: filled,
      total: '$960',
      totalLabel: 'Total including the optional gutter faces',
      note: 'Figures are illustrative for one job, not price guidance.',
    },
    {
      type: 'list',
      id: 'tips',
      h2: 'Getting each section right',
      style: 'steps',
      items: [
        { title: 'Measure each surface', text: 'Length times height for siding sides, length times width for flatwork. Write the size on the line so a re-quote next year is quick.' },
        { title: 'Name the method', text: 'Customers worry about high pressure on siding and roofs. Saying "soft wash" on those lines answers the question before it is asked.' },
        { title: 'List extras separately', text: 'Gutter faces, stain treatment and re-sanding are where an upsell lives. Separate lines let the customer choose.' },
        { title: 'Ask about water early', text: 'The site conditions section is there because a dead outdoor tap ends the day.' },
      ],
    },
    {
      type: 'text',
      id: 'roof',
      h2: 'A note on roofs and wood',
      paragraphs: [
        'Roofs and wood decks need gentler handling and more time than concrete, and they carry more risk. Keep them on their own lines with their own price, and add a sentence about what the customer should expect: shingles cleaned of algae streaks, not restored; wood brightened, not refinished.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Pressure washing quote template FAQ', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/pressure-washing', anchor: 'Scopie for pressure washing businesses', status: 'live' },
    { href: '/resources/how-to-price-pressure-washing', anchor: 'how to price pressure washing', status: 'live' },
    { href: '/templates/cancellation-policy', anchor: 'cancellation policy template', status: 'live' },
  ],
  cta: {
    heading: 'Send this quote as a link instead of a PDF',
    text: 'Load your surfaces and clauses into Scopie once, and email each exterior quote from the driveway.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
